import { ArrowRightAlt } from '@mui/icons-material';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';

import { useSxStyles } from './useStyles';

export type EmploymentUpcomingChangeDifference = {
  key: string;
  label?: string;
  groupLabel?: string;
  oldValue?: number | boolean | string;
  newValue?: number | boolean | string;
};

type ChangeDiffGroup = {
  label: string;
  changes: EmploymentUpcomingChangeDifference[];
};

type ChangeDiffGroupings = {
  [P: string]: ChangeDiffGroup;
};

type PropsType = {
  label: string;
  valueChanges: EmploymentUpcomingChangeDifference[];
  children?: React.ReactNode;
};

export const EmploymentUpcomingChanges = (props: PropsType) => {
  const { valueChanges, label: heading, children } = props;
  const sxStyles = useSxStyles();

  const groupedChanges: ChangeDiffGroupings = useMemo(
    () =>
      valueChanges.reduce(
        (
          acc: ChangeDiffGroupings,
          change: EmploymentUpcomingChangeDifference,
        ) => {
          const groupIds = Object.keys(acc);
          const groupId = !!change.groupLabel ? change.groupLabel : 'default';
          const previousDiffs: EmploymentUpcomingChangeDifference[] =
            groupIds.includes(groupId) ? acc[groupId].changes : [];

          return {
            ...acc,
            [groupId]: {
              label: !!change.groupLabel ? change.groupLabel : '',
              changes: [...previousDiffs, change],
            },
          };
        },
        {},
      ),
    [valueChanges],
  );

  return (
    <Paper>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={sxStyles.updateHeading}
      >
        <Typography variant={'subtitle1'}>{heading}</Typography>
        {!!children && <Box>{children}</Box>}
      </Stack>

      {Object.entries(groupedChanges).map(([groupKey, group], index) => {
        return (
          <Fragment key={`group-${index}`}>
            <Divider />
            <Box sx={sxStyles.updateGroup}>
              <Typography variant={'subtitle2'}>{group.label}</Typography>
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                justifyContent="flex-start"
                rowGap={1}
                columnGap={1}
                width={'100%'}
              >
                {group.changes.map((valueChange) => {
                  const { key, label, oldValue, newValue } = valueChange;
                  return (
                    <Stack
                      key={`${key}-${index}`}
                      spacing={1}
                      direction="row"
                      alignItems={'center'}
                    >
                      <Box sx={sxStyles.valueLabel}>{label ?? key}:</Box>
                      <Typography sx={sxStyles.valueOld}>{oldValue}</Typography>
                      <ArrowRightAlt sx={sxStyles.valueArrow} />
                      <Typography sx={sxStyles.valueNew}>{newValue}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Fragment>
        );
      })}
    </Paper>
  );
};
